import {Component, EventEmitter, HostListener, Inject, OnInit, Output} from '@angular/core';
import { AuthService } from 'src/app/@auth/auth.service';
import { CluNumpadDialogService } from '../clu-numpad-dialog/clu-numpad-dialog.service';
import { Subscription, lastValueFrom } from 'rxjs';
import { CluPMediaOptions } from 'src/app/@clu-be/apps/@models/clupmediaoptions.model';
import { MOBILE_VIEW, MOBILE_VIEW_L, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR, BELOWMONITOR_L, BELOWMONITOR_M, BELOWMONITOR_L1100 } from 'src/app/app.models';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CluLockscreenAccountPickerDialog } from './@modals/clu-lockscreen-account-picker-dialog/clu-lockscreen-account-picker-dialog.component';
import { CashDeskCoreRxDbService } from 'src/app/@clu-fe/apps/m-cashdesk/@services/m-cashdesk-core-rxdb.service';
import * as crypto from "crypto-js";
import { LOCKSCREEN_MODE } from './clu-lockscreen.service';
import { CoreService } from 'src/app/services/core.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CluConfirmDialogService } from '../clu-confirm-dialog/clu-confirm-dialog.service';
import { CluNumpadDialogComponent } from '../clu-numpad-dialog/clu-numpad-dialog.component';

@Component({
    selector: 'clu-lockscreen',
    templateUrl: './clu-lockscreen.component.html',
    styleUrls: ['./clu-lockscreen.component.scss'],
})
export class CluLockscreenComponent implements OnInit {
    @Output() onResult = new EventEmitter<string | null>();
    loading = false;
    errorText = '';
    codepin: string[] = [];
    mediaOptions : CluPMediaOptions = new CluPMediaOptions();
    usersAccounts: any[] = [];
    activeUserAccount: any;
    lockscreenMode = LOCKSCREEN_MODE.LOCK_SESSION;
    LOCKSCREEN_MODE = LOCKSCREEN_MODE;
    private layoutChangesSubscription = Subscription.EMPTY;

    constructor(
        private dialog: MatDialog,
        private authService: AuthService,
        private cluNumpadDialogService: CluNumpadDialogService,
        private cashDeskRxDbService : CashDeskCoreRxDbService,
        private breakpointObserver: BreakpointObserver,
        private coreService: CoreService,
        private snackbar: MatSnackBar,
        private router: Router,
        private cluConfirmDialogService: CluConfirmDialogService,
        private matDialogRef: MatDialogRef<CluNumpadDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: { lockscreenMode?: LOCKSCREEN_MODE }
    ) {
        if (!!data.lockscreenMode) {
            this.lockscreenMode = data.lockscreenMode;
        }
        // BREAKPOINTS
        this.layoutChangesSubscription = this.breakpointObserver
        .observe([MOBILE_VIEW, MOBILE_VIEW_L, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR, BELOWMONITOR_L, BELOWMONITOR_M, BELOWMONITOR_L1100])
        .subscribe((state) => {
          this.mediaOptions.LAYOUT_MOBILE_VIEW_L = state.breakpoints[MOBILE_VIEW_L];
          this.mediaOptions.LAYOUT_MOBILE_VIEW = state.breakpoints[MOBILE_VIEW];
          this.mediaOptions.LAYOUT_BELOWMONITOR = state.breakpoints[BELOWMONITOR];
          this.mediaOptions.LAYOUT_BELOWMONITOR_L = state.breakpoints[BELOWMONITOR_L];
          this.mediaOptions.LAYOUT_BELOWMONITOR_L1100 = state.breakpoints[BELOWMONITOR_L1100];
          this.mediaOptions.LAYOUT_BELOWMONITOR_M = state.breakpoints[BELOWMONITOR_M];
        });
    }

    ngOnInit() {
        this.resolveUsersAccounts();
    }

    private async resolveUsersAccounts() {
        let usersAccounts: any[] = [];
        let activeUserAccount: any;
        if (this.lockscreenMode == LOCKSCREEN_MODE.LOCK_SESSION) {
            const authenticatedUserAccount = await this.resolveAuthenticatedUserAccount();
            if (!!authenticatedUserAccount) {
                usersAccounts.push(authenticatedUserAccount);
                activeUserAccount = authenticatedUserAccount;
            }
        } else if (this.lockscreenMode == LOCKSCREEN_MODE.ASK_FOR_PERMISSION) {
            const cashdeskSuperviseursAccounts = await this.resolveActiveCashdeskSuperviseursAccounts();
            usersAccounts = usersAccounts.concat(cashdeskSuperviseursAccounts);
            if (usersAccounts.length > 0) {
                activeUserAccount = usersAccounts[0];
            }
        }
        this.usersAccounts = usersAccounts;
        this.activeUserAccount = activeUserAccount;
    }

    private async resolveAuthenticatedUserAccount() {
        const authSession = this.authService.authenticatedSession$.value;
        if (!!authSession) {
            return { ID: authSession.ID, USERNAME: authSession.USERNAME, CODE_PIN: authSession.CODE_PIN };
        }
        return null;
    }

    private async resolveActiveCashdeskSuperviseursAccounts() {
        let cashdeskSuperviseurs = [];
        const cashdeskGID = localStorage.getItem('LS_FE_AUTH_CASHDESK_GID')
        const activeCashdesk = await this.cashDeskRxDbService.db.pdevice.findOne({ 
            selector: { "CAISSEGID": { "$eq": cashdeskGID } }, 
            sort: [] 
        }).exec();
        if (!!activeCashdesk && !!activeCashdesk.DEVICES_SUPERVISEURS) {
            try { cashdeskSuperviseurs = JSON.parse(activeCashdesk.DEVICES_SUPERVISEURS); } catch {}
        }

        return cashdeskSuperviseurs.map((superviseur: any) => ({ ID: superviseur.ID, USERNAME: superviseur.USERNAME, CODE_PIN: superviseur.CODE_PIN }));
    }

    @HostListener('window:keyup', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if(event.keyCode == 8) {
            this.codepin.pop()
        } else if(this.codepin.length < 6 && (event.key.length == 1 || (event.key.length > 1 && /[^a-zA-Z0-9]/.test(event.key)))) {
            this.codepin.push(event.key.toUpperCase());
            if (this.codepin.length === 6) {
                this.validateCodepin();
            }
        }
    }

    async openNumpadDialog() {
        const inputCodepin = await this.cluNumpadDialogService.openNumpadDialog({ randomize: true, length: 6 });
        if (!!inputCodepin) {
            this.codepin = inputCodepin.split('');
            this.validateCodepin();
        }
    }

    private validateCodepin() {
        this.loading = true;
        this.errorText = '';
        setTimeout(() => {
            try {
                if (!this.activeUserAccount) { throw 'Aucun compte sélectionné'; }
                let sessionCodepin = this.activeUserAccount.CODE_PIN;
                let inputCodepin= crypto.SHA1(this.codepin.join('')).toString();
                if (!!sessionCodepin) { sessionCodepin = sessionCodepin.toUpperCase(); }
                if (!!inputCodepin) { inputCodepin = inputCodepin.toUpperCase(); }
                if (sessionCodepin != inputCodepin) { throw 'Code PIN incorrect'; }

                this.codepin = [];
                this.loading = false;

                if (this.lockscreenMode == LOCKSCREEN_MODE.ASK_FOR_PERMISSION) {
                    this.matDialogRef.close(this.activeUserAccount.ID);
                } else if (this.lockscreenMode == LOCKSCREEN_MODE.LOCK_SESSION) {
                    this.authService.doUnlockSession();
                }
            } catch(e: any) {
                this.errorText = e;
                this.codepin = [];
                this.loading = false;
            }
        }, 1000);
    }

    openAccountPickerDialog() {
        let CC_WIDTH = this.mediaOptions.LAYOUT_BELOWMONITOR ? '100%' : '600px';
        let CC_HEIGHT = this.mediaOptions.LAYOUT_BELOWMONITOR ? '100%' : window.innerHeight && window.innerHeight < 800 ? '100%' : '80vh';
        const dialogRef = this.dialog.open(CluLockscreenAccountPickerDialog, { 
            width: '100%', maxWidth: CC_WIDTH, minHeight: CC_HEIGHT, maxHeight: CC_HEIGHT, height : '100%', 
            data: { UsersAccounts: this.usersAccounts, ActiveUserAccount: this.activeUserAccount } 
        });
        dialogRef.afterClosed().subscribe((result?: any) => {
            if (!result) { return; }
            this.activeUserAccount = result;
        });
    }

    exitScreen() {
        this.matDialogRef.close();
    }

    async doLogout() {
        const isConfirm = await this.cluConfirmDialogService.showConfirmDialog('Session vérouillée', 'Voulez-vous vraiment vous déconnecter ?');
        if (isConfirm) {
            this.dialog.closeAll();
            const uiWorkspace = this.coreService.getUIWorkspace();
            if (uiWorkspace == 'fe-workspace') {
                this.authService.doSignoutFOAction();
                this.authService.doRemoveAuthSession();
                
                this.router.navigateByUrl('/login');
            } else if (uiWorkspace == 'be-workspace') {
                this.authService.doSignoutAdminAction();
                this.authService.doRemoveAuthSession();
                this.router.navigateByUrl("/cluadmin/login");
            } else {
                this.authService.doRemoveAuthSession();
                this.router.navigateByUrl('/login');
            }
            this.snackbar.open("Déconnecté", undefined, { duration: 3000 });
        }
    }
}